import { motion } from 'framer-motion';
import { Shield } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

export default function PrivacyPolicyPage() {
  return (
    <div data-testid="privacy-policy-page" className="bg-[#0E2A47] min-h-screen">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 grid-overlay" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <div className="w-16 h-16 rounded-sm bg-[#0FA4A9]/10 flex items-center justify-center mx-auto">
                <Shield className="h-8 w-8 text-[#0FA4A9]" />
              </div>
            </motion.div>
            <motion.h1 
              variants={fadeInUp}
              className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4"
            >
              Privacy Policy
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="font-body text-white/60"
            >
              Effective Date: February 15, 2026 | Last Updated: February 15, 2026
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-[#0A1F35]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="prose prose-invert max-w-none"
          >
            <div className="glass-card rounded-sm p-8 sm:p-12 space-y-10">
              {/* Intro */}
              <p className="font-body text-white/80 text-lg leading-relaxed">
                Discovify AI ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </p>

              {/* Section 1 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  1. INFORMATION WE COLLECT
                </h2>
                
                <h3 className="font-heading text-lg font-semibold text-[#0FA4A9] mb-3">
                  Personal Information You Provide:
                </h3>
                <p className="font-body text-white/70 mb-4">
                  When you interact with our website, we may collect:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70 mb-6">
                  <li>Name and contact information (email address, phone number)</li>
                  <li>Company name and job title</li>
                  <li>LinkedIn profile URL</li>
                  <li>Calendar booking information</li>
                  <li>Information you provide during discovery calls</li>
                  <li>Information submitted through forms and contact requests</li>
                  <li>Communication preferences</li>
                </ul>

                <h3 className="font-heading text-lg font-semibold text-[#0FA4A9] mb-3">
                  Automatically Collected Information:
                </h3>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70 mb-6">
                  <li>Browser type and version</li>
                  <li>IP address</li>
                  <li>Device information</li>
                  <li>Pages visited and time spent on pages</li>
                  <li>Referral source</li>
                </ul>

                <h3 className="font-heading text-lg font-semibold text-[#0FA4A9] mb-3">
                  Cookies and Tracking Technologies:
                </h3>
                <p className="font-body text-white/70">
                  We may use cookies, web beacons, and similar technologies to enhance your experience and collect usage data. You can control cookie preferences through your browser settings.
                </p>
              </div>

              {/* Section 2 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  2. HOW WE USE YOUR INFORMATION
                </h2>
                <p className="font-body text-white/70 mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70">
                  <li>Respond to your inquiries and provide requested services</li>
                  <li>Schedule and conduct discovery calls and consultations</li>
                  <li>Send automated email communications (with your consent)</li>
                  <li>Provide updates about our services, products, and offerings</li>
                  <li>Improve our website and service offerings</li>
                  <li>Analyze usage patterns and optimize user experience</li>
                  <li>Send marketing communications about AI automation services and workshops</li>
                  <li>Gauge interest in new services (such as AI recruiting marketplace)</li>
                  <li>Maintain records of our business interactions</li>
                </ul>
              </div>

              {/* Section 3 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  3. AUTOMATED COMMUNICATIONS
                </h2>
                <p className="font-body text-white/70 mb-4">
                  By submitting forms on our website or booking calls, you consent to receive:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70 mb-4">
                  <li>Automated confirmation emails</li>
                  <li>Follow-up emails related to your inquiry</li>
                  <li>Appointment reminders</li>
                  <li>Service updates and newsletters</li>
                  <li>Marketing communications about our services</li>
                </ul>
                <p className="font-body text-white/70">
                  You can opt-out of marketing emails at any time using the unsubscribe link in any email or by contacting us directly.
                </p>
              </div>

              {/* Section 4 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  4. HOW WE SHARE YOUR INFORMATION
                </h2>
                <p className="font-body text-white/70 mb-4">
                  <strong className="text-white">We do not sell your personal information to third parties.</strong>
                </p>
                <p className="font-body text-white/70 mb-4">
                  We may share your information with:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70 mb-6">
                  <li>Service providers who assist us in operating our website and delivering services (e.g., calendar booking platforms, email service providers, CRM systems)</li>
                  <li>Professional advisors (lawyers, accountants) when necessary</li>
                  <li>Law enforcement or government agencies when required by law</li>
                </ul>

                <h3 className="font-heading text-lg font-semibold text-[#0FA4A9] mb-3">
                  Third-Party Service Providers:
                </h3>
                <p className="font-body text-white/70 mb-4">
                  We may use the following types of service providers who have access to your information:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70">
                  <li>Email marketing platforms (e.g., for automated emails)</li>
                  <li>Calendar and scheduling tools</li>
                  <li>Customer relationship management (CRM) systems</li>
                  <li>Analytics providers</li>
                  <li>Hosting and infrastructure providers</li>
                </ul>
              </div>

              {/* Section 5 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  5. DATA RETENTION
                </h2>
                <p className="font-body text-white/70 mb-4">
                  We retain your personal information for as long as necessary to:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70 mb-4">
                  <li>Fulfill the purposes outlined in this Privacy Policy</li>
                  <li>Comply with legal obligations</li>
                  <li>Resolve disputes</li>
                  <li>Enforce our agreements</li>
                </ul>
                <p className="font-body text-white/70">
                  You may request deletion of your information at any time by contacting us.
                </p>
              </div>

              {/* Section 6 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  6. YOUR RIGHTS AND CHOICES
                </h2>
                <p className="font-body text-white/70 mb-4">
                  You have the right to:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70 mb-4">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your information</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Withdraw consent for automated emails</li>
                  <li>Object to processing of your information</li>
                </ul>
                <p className="font-body text-white/70">
                  To exercise these rights, contact us at: <a href="mailto:info@discovify.ai" className="text-[#0FA4A9] hover:underline">info@discovify.ai</a>
                </p>
              </div>

              {/* Section 7 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  7. DATA SECURITY
                </h2>
                <p className="font-body text-white/70">
                  We implement reasonable security measures to protect your information from unauthorized access, disclosure, alteration, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
                </p>
              </div>

              {/* Section 8 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  8. CHILDREN'S PRIVACY
                </h2>
                <p className="font-body text-white/70">
                  Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.
                </p>
              </div>

              {/* Section 9 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  9. LINKS TO OTHER WEBSITES
                </h2>
                <p className="font-body text-white/70">
                  Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
                </p>
              </div>

              {/* Section 10 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  10. CALIFORNIA PRIVACY RIGHTS
                </h2>
                <p className="font-body text-white/70 mb-4">
                  California residents have additional rights under the California Consumer Privacy Act (CCPA), including:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70">
                  <li>Right to know what personal information is collected</li>
                  <li>Right to delete personal information</li>
                  <li>Right to opt-out of sale of personal information (note: we do not sell personal information)</li>
                </ul>
              </div>

              {/* Section 11 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  11. CHANGES TO THIS PRIVACY POLICY
                </h2>
                <p className="font-body text-white/70">
                  We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on this page with a new "Last Updated" date.
                </p>
              </div>

              {/* Section 12 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  12. CONTACT US
                </h2>
                <p className="font-body text-white/70 mb-4">
                  If you have questions about this Privacy Policy or how we handle your information, contact us at:
                </p>
                <div className="bg-[#1E4B75]/50 rounded-sm p-6">
                  <p className="font-heading text-lg font-bold text-white mb-2">Discovify AI</p>
                  <p className="font-body text-white/70">
                    Email: <a href="mailto:info@discovify.ai" className="text-[#0FA4A9] hover:underline">info@discovify.ai</a>
                  </p>
                  <p className="font-body text-white/70">
                    Website: <a href="https://www.discovify.ai" className="text-[#0FA4A9] hover:underline">www.discovify.ai</a>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
